<template>
  <div
    class="mb-2 flex h-9 w-full items-center rounded-md py-1 pl-4 font-medium text-surface-700 transition-colors duration-200 hover:bg-surface-100"
    data-test="project-item"
    @mouseenter.stop="is_hovering = true"
    @mouseleave.stop="is_hovering = false"
  >
    <AppLink
      :to="target_route"
      hide_underline
      class="flex grow items-center gap-2 truncate no-underline"
      @click="emit('closeProjectSidebar')"
    >
      <span
        v-if="project.attributes.skip_customer"
        class="flex size-6.25 shrink-0 items-center justify-center rounded-full bg-gray-200"
      >
        <FontAwesomeIcon
          class="text-sm text-gray-500"
          :icon="['far', 'memo']"
        />
      </span>

      <avatar
        v-else
        class="shrink-0"
        size="25"
        :src="project_avatar_url"
        :username="project_avatar_name"
      />

      <div class="ml-1 shrink truncate text-surface-700">
        {{ project.attributes.name }}
      </div>

      <span
        v-if="!project.attributes.skip_customer && !project.attributes.activated"
        class="shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600"
      >
        {{ $t('project.labels.deactivated') }}
      </span>
    </AppLink>

    <ButtonSettings
      v-if="is_hovering || is_popover_open"
      ref="button_settings_ref"
      class="shrink-0"
      :subtle="true"
      :loading="is_follow_pending"
      @open="
        is_popover_open = true;
        active_popover_project_id = project.id;
      "
      @close="is_popover_open = false"
    >
      <DropdownOption
        v-for="action in overlay_actions"
        :key="action.label"
        :disabled="action.disabled"
        @click="action.action"
      >
        {{ action.label }}
      </DropdownOption>
    </ButtonSettings>
  </div>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { Assignment, PROFILE_TYPES } from '@/app/data/model_constants';
import { ROUTES_EMPLOYEES } from '@/app/data/route_constants';
import { useI18n } from 'vue-i18n';
import { useToasts } from '@/app/util/toasts';
import useGlobalConfirm from '@/composables/useGlobalConfirm';
import ButtonSettings from '@/app/shared_components/buttons/ButtonSettings.vue';
import DropdownOption from '@/app/shared_components/buttons/DropdownOption.vue';

const { toast_action_failed, toast_action_succeeded } = useToasts();

const { t } = useI18n();

const props = defineProps({
  project: { type: Object, required: true },
  is_following_project: { type: Boolean },
  project_ids_follow_pending: { type: Object, default: () => ({}) },
  assignments: { type: Object, default: null },
  is_template: { type: Boolean },
});

const emit = defineEmits(['unfollow', 'follow', 'closeProjectSidebar']);

const is_hovering = ref(false);
const is_popover_open = ref(false);
const button_settings_ref = ref(null);
const active_popover_project_id = inject('active_popover_project_id', ref(null));

watch(active_popover_project_id, (new_id) => {
  if (new_id !== props.project.id && is_popover_open.value) {
    button_settings_ref.value.close();
  }
});

const { confirm } = useGlobalConfirm();

const store = useStore();
const route = useRoute();
const router = useRouter();

const current_profile = computed(() => store.getters.current_profile);
const current_project = computed(() => store.getters.current_project);
const is_follow_pending = computed(() => !!props.project_ids_follow_pending[props.project.id]);

const assignment = computed(() => {
  const attributes = {
    assigned_id: props.project.id,
    assigned_type: props.project.type,
    assignee_id: current_profile.value.id,
    assignee_type: current_profile.value.type,
    assignment_type: Assignment.TYPE_FOLLOW,
  };
  const assignments = _.filter(props.assignments, { attributes });
  return assignments[0] || null;
});

const project_avatar_url = computed(() => {
  const attributes = props.project.attributes;

  if (attributes.picture_login_url_s) return attributes.picture_login_url_s;

  if (attributes.skip_unit) {
    return attributes.customer_profile.avatar || null;
  }

  return null;
});

const project_avatar_name = computed(() => props.project.attributes.name);

function toggleFollow() {
  if (assignment.value) {
    emit('unfollow', assignment.value);
  } else {
    const assign = {
      attributes: {
        assigned_id: props.project.id,
        assigned_type: props.project.type,
        assignee_id: current_profile.value.id,
        assignee_type: current_profile.value.type,
        project_id: props.project.id,
        assignment_type: Assignment.TYPE_FOLLOW,
      },
    };
    if (current_profile.value.type === PROFILE_TYPES.PARTNER) {
      assign.attributes.client_id = props.project.attributes.client_id;
    }
    emit('follow', assign);
  }
}

async function toggleActivation() {
  try {
    await store.dispatch('UPDATE_NON_CURRENT_PROJECT', {
      record: {
        id: props.project.id,
        attributes: { activated: !props.project.attributes.activated },
      },
      purge: false,
    });
    const i18n = props.project.attributes.activated
      ? 'project.toasts.activateSuccess'
      : 'project.toasts.deactivateSuccess';
    toast_action_succeeded(t(i18n, { project_name: props.project.attributes.name }));
    if (current_project.value.id === props.project.id) {
      router.go();
    }
  } catch (e) {
    toast_action_failed({ error: e });
  }
}

const target_route = computed(() => {
  const { id, type } = props.project;
  const name = ROUTES_EMPLOYEES.PROJECT_INFORMATION_LEVELS_INFORMATION;
  const params = { project_id: id, level_type: 'Project', level_id: id };

  return {
    name,
    params,
  };
});

async function toggleDeletion() {
  const confirmed = await confirm({
    destructive: true,
    header: t('modal.labels.removeTitle', { record_name: props.project.attributes.name }),
    message: t('levels.labels.deleteWarning'),
    object_name: props.project.attributes.name,
    accept_label: t('levels.actions.delete'),
  });
  if (confirmed) await deleteProject();
}

async function deleteProject() {
  await store.dispatch('DELETE_PROJECT', { record: props.project });
  router.push({ name: ROUTES_EMPLOYEES.CLIENT_DASHBOARD });
}

const overlay_actions = computed(() => {
  const actions = [];
  if (!props.is_template) {
    actions.push({
      label: props.is_following_project ? t('project.actions.unFollow') : t('project.actions.follow'),
      disabled: is_follow_pending.value,
      action: toggleFollow,
    });
    if (props.project.attributes.rundle) {
      actions.push({
        label: props.project.attributes.activated ? t('project.actions.deactivate') : t('project.actions.activate'),
        action: toggleActivation,
      });
    }
  } else {
    actions.push({
      label: t('levels.actions.delete'),
      action: toggleDeletion,
    });
  }
  return actions;
});
</script>
